import {
  ABILITY_RULES,
  ACTIVE_COOLDOWN_TICKS,
  BUILDINGS,
  CATALOGUE,
  CELL_SIZE,
  DIFFICULTIES,
  FIXED_TICK_HZ,
  GRID_SIZE,
  LEAK_LIFE_DAMAGE,
  LOGICAL_BOARD_SIZE,
  MAPS,
  PROJECTILE_HIT_RADIUS,
  SCORE_PER_REWARD,
  SELL_FRACTION,
  STRUCTURE_FOOTPRINT_CELLS,
  TOWER_FIRE_ALIGNMENT_RADIANS,
  TOWERS,
  WAVE_SCRIPTS,
  enemyDefinition,
  isTowerId,
  structureLabel,
} from '../content/data';
import type { AbilityId, BuildingId, CatalogueId, DifficultyId, MapDefinition, MapId, TowerId, WaveDefinition, WaveGroup } from '../content/types';
import { angleTo, approachAngle, distanceSquared, moveTowards, wrapAngle } from './math';
import type { BuildingState, EnemyState, ProjectileState, RunMode, RunState, SimEvent, SimulationSnapshot, TowerState, UnlockProgress, Vec2, VisualEvent, WallState } from './types';
import { resolveWave, scaleEnemyHealth, waveMetadata } from './waveResolver';

const MAX_EVENTS = 12;
const MAX_VISUAL_EVENTS = 64;
const TURN_CURVE_SAMPLES = 12;
const TICK_SECONDS = 1 / FIXED_TICK_HZ;
const CONFUSE_TURN_MULTIPLIER = 1 - TOWERS['cd1.tower.oil'].confuse!.percent;

const cloneVec = (value: Vec2): Vec2 => ({ x: value.x, y: value.y });
const cellKey = (x: number, y: number) => `${x},${y}`;
const abilityIds: readonly AbilityId[] = ['missile', 'wall', 'stop-time'];

/**
 * Rules-only fixed-tick simulation. Phaser and DOM code only read snapshots and dispatch actions here.
 * Deterministic ordering per tick: spawn -> movement -> targeting/fire -> projectile impact -> rewards/leaks -> wave result.
 */
export class Simulation {
  private state: RunState;
  private nextEntityId = 1;
  private nextVisualEventId = 1;
  private randomState: number;

  constructor(mapId: MapId, difficultyId: DifficultyId, seedOrRunMode: number | RunMode = 0xced10001, runMode: RunMode = 'fixed') {
    const seed = typeof seedOrRunMode === 'number' ? seedOrRunMode : 0xced10001;
    const selectedRunMode = typeof seedOrRunMode === 'string' ? seedOrRunMode : runMode;
    this.randomState = seed >>> 0;
    this.state = {
      tick: 0,
      seed,
      mapId,
      difficultyId,
      runMode: selectedRunMode,
      phase: 'prewave',
      paused: false,
      cash: DIFFICULTIES[difficultyId].startingCash.value,
      lives: DIFFICULTIES[difficultyId].startingLives.value,
      score: 0,
      completedWaves: 0,
      nextWaveIndex: 0,
      nextWaveCountdownTicks: DIFFICULTIES[difficultyId].waveIntervalTicks.value,
      activeWave: null,
      enemies: [],
      towers: [],
      buildings: [],
      walls: [],
      projectiles: [],
      counters: { builtMissiles: 0, builtAA: 0, builtTotal: 0, destroyedAir: 0, kills: 0, earned: 0 },
      events: [],
      visualEvents: [],
      unlocked: Object.fromEntries(CATALOGUE.map((entry) => [entry.id, entry.id === 'cd1.tower.missile' || entry.id === 'cd1.tower.aa'])),
      abilityCooldowns: { missile: 0, wall: 0, 'stop-time': 0 },
      missileActions: 0,
      lastNotice: 'Choose a defense, then place it on solid canyon ground.',
    };
  }

  getSnapshot(): SimulationSnapshot {
    const { state } = this;
    const nextWave = this.resolveWave(state.nextWaveIndex + 1);
    return {
      tick: state.tick,
      mapId: state.mapId,
      difficultyId: state.difficultyId,
      runMode: state.runMode,
      phase: state.phase,
      paused: state.paused,
      cash: state.cash,
      lives: state.lives,
      score: state.score,
      completedWaves: state.completedWaves,
      nextWaveIndex: state.nextWaveIndex,
      nextWave: nextWave ? waveMetadata(nextWave) : null,
      nextWaveCountdownTicks: state.nextWaveCountdownTicks,
      activeWave: state.activeWave ? { ...state.activeWave } : null,
      enemyCount: state.enemies.length,
      towerCount: state.towers.length,
      buildingCount: state.buildings.length,
      wallCount: state.walls.length,
      projectileCount: state.projectiles.length,
      counters: { ...state.counters },
      unlocked: { ...state.unlocked },
      progress: this.getUnlockProgress().map((progress) => ({ ...progress })),
      abilityCooldowns: { ...state.abilityCooldowns },
      visualEvents: state.visualEvents.map((event) => ({
        ...event,
        position: cloneVec(event.position),
        ...(event.target ? { target: cloneVec(event.target) } : {}),
      })),
      lastNotice: state.lastNotice,
    };
  }

  getState(): Readonly<RunState> { return this.state; }

  getTickSeconds(): number { return TICK_SECONDS; }

  /** Test-only fixture setup used by the browser QA hook; never reachable from game input. */
  debugPrimeVisualQa(): void {
    this.state.cash = 10_000;
    Object.keys(this.state.unlocked).forEach((id) => { this.state.unlocked[id] = true; });
    this.notice('notice', 'QA fixture armed.');
  }

  /** Test-only unlock transition used by browser presentation QA. */
  debugUnlockForQa(definitionId: CatalogueId): boolean {
    if (this.state.unlocked[definitionId]) return false;
    this.state.unlocked[definitionId] = true;
    return true;
  }

  isUnlocked(definitionId: CatalogueId): boolean { return this.state.unlocked[definitionId] === true; }

  abilityReady(ability: AbilityId): boolean {
    return this.abilitySourceCount(ability) > 0 && this.state.abilityCooldowns[ability] <= 0;
  }

  getAbilitySourceCount(ability: AbilityId): number { return this.abilitySourceCount(ability); }

  getEffectiveTowerRange(definitionId: TowerId): number {
    return TOWERS[definitionId].range.value * (10 + this.countBuildings('cd1.building.radar')) / 10;
  }

  getUnlockProgress(): readonly UnlockProgress[] {
    const state = this.state;
    const values: Record<string, { current: number; target: number }> = {
      'cd1.tower.howitzer': { current: state.missileActions, target: 1 },
      'cd1.tower.tesla': { current: this.countTowers('cd1.tower.goo'), target: 5 },
      'cd1.tower.gatling': { current: state.counters.destroyedAir, target: 40 },
      'cd1.tower.nulificator': { current: this.countBuildings('cd1.building.laboratory'), target: 1 },
      'cd1.tower.goo': { current: state.counters.destroyedAir, target: 5 },
      'cd1.tower.chain': { current: state.cash, target: 1000 },
      'cd1.tower.gauss': { current: this.countBuildings('cd1.building.radar'), target: 1 },
      'cd1.tower.oil': { current: state.walls.length, target: 10 },
      'cd1.building.silo': { current: this.countTowers('cd1.tower.missile'), target: 5 },
      'cd1.building.wall-factory': { current: this.countTowers() + this.countBuildings(), target: 15 },
      'cd1.building.recycler': { current: this.countTowers('cd1.tower.tesla') + this.countTowers('cd1.tower.chain'), target: 3 },
      'cd1.building.radar': { current: this.countTowers('cd1.tower.aa'), target: 7 },
      'cd1.building.laboratory': { current: this.countBuildings('cd1.building.wall-factory'), target: 2 },
    };
    const chains: readonly (readonly string[])[] = [
      ['cd1.tower.howitzer', 'cd1.tower.tesla', 'cd1.tower.gatling', 'cd1.tower.nulificator'],
      ['cd1.tower.goo', 'cd1.tower.chain', 'cd1.tower.gauss', 'cd1.tower.oil'],
      ['cd1.building.silo', 'cd1.building.wall-factory', 'cd1.building.recycler', 'cd1.building.radar', 'cd1.building.laboratory'],
    ];
    return chains.flatMap((chain) => chain.map((id, index) => {
      const unlocked = state.unlocked[id] === true;
      const predecessorUnlocked = index === 0 || state.unlocked[chain[index - 1]] === true;
      return { id, ...values[id], unlocked, active: !unlocked && predecessorUnlocked };
    }));
  }

  startNextWave(): boolean {
    if (this.state.paused) return false;
    if (!this.resolveWave(this.state.nextWaveIndex + 1)) return false;
    const skippedCountdown = Math.max(0, this.state.nextWaveCountdownTicks);
    if (this.state.phase === 'active') {
      this.creditSkippedCooldowns(skippedCountdown);
      return this.beginNextWave(true);
    }
    if (this.state.phase !== 'prewave') return false;
    this.creditSkippedCooldowns(skippedCountdown);
    return this.beginNextWave(true);
  }

  private beginNextWave(earlyRelease: boolean): boolean {
    const resolved = this.resolveWave(this.state.nextWaveIndex + 1);
    if (!resolved || (this.state.phase !== 'prewave' && this.state.phase !== 'active')) return false;
    const index = this.state.nextWaveIndex;
    const selected = resolved.definition;
    this.state.nextWaveIndex += 1;
    this.state.phase = 'active';
    this.state.activeWave = {
      index,
      ...waveMetadata(resolved),
      spawned: 0,
      total: selected.groups.reduce((total, group) => total + group.count, 0),
      nextSpawnTick: this.state.tick,
      groupIndex: 0,
      groupSpawned: 0,
    };
    this.state.nextWaveCountdownTicks = this.resolveWave(this.state.nextWaveIndex + 1) ? DIFFICULTIES[this.state.difficultyId].waveIntervalTicks.value : 0;
    // Program rows create their complete group together. Spawn now so a second PLAY release cannot replace an
    // unspawned active row, and so early releases genuinely overlap the units already in the field.
    this.spawnDueEnemies();
    const cycleNotice = resolved.completedCycles > 0 && resolved.baseWave === 1
      ? ` Cycle ${resolved.cycle} begins: enemy HP ${resolved.hpPercent}%.`
      : '';
    this.notice('wave', `Attack ${resolved.absoluteWave} started${earlyRelease ? ' early' : ''}.${cycleNotice}`);
    return true;
  }

  setPaused(paused: boolean): boolean {
    if (this.state.phase !== 'prewave' && this.state.phase !== 'active') return false;
    this.state.paused = paused;
    this.notice('notice', paused ? 'Simulation paused.' : 'Simulation resumed.');
    return true;
  }

  togglePaused(): boolean { return this.setPaused(!this.state.paused); }

  placeTower(definitionId: TowerId, x: number, y: number): boolean { return this.placeStructure(definitionId, x, y); }

  placeStructure(definitionId: CatalogueId, x: number, y: number): boolean {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return false;
    const definition = isTowerId(definitionId) ? TOWERS[definitionId] : BUILDINGS[definitionId];
    const cell = this.toCell(x, y);
    if (!definition) return false;
    if (!this.isUnlocked(definitionId)) {
      this.notice('notice', 'Placement rejected: construction chain is still locked.');
      return false;
    }
    if (!this.canPlaceStructure(definitionId, x, y)) {
      this.notice('notice', 'Placement rejected: the full footprint needs clear solid canyon ground.');
      return false;
    }
    if (this.state.cash < definition.cost.value) {
      this.notice('notice', 'Placement rejected: insufficient credits.');
      return false;
    }
    this.state.cash -= definition.cost.value;
    if (isTowerId(definitionId)) {
      const towerDefinition = TOWERS[definitionId];
      const tower: TowerState = {
        id: this.nextEntityId++, definitionId,
        position: { x: cell.x * CELL_SIZE + CELL_SIZE / 2, y: cell.y * CELL_SIZE + CELL_SIZE / 2 },
        cell, angle: -Math.PI / 2, cooldownTicks: towerDefinition.reloadTicks.value, targetId: null, cost: towerDefinition.cost.value,
      };
      this.state.towers = [...this.state.towers, tower];
      if (definitionId === 'cd1.tower.missile') this.state.counters.builtMissiles += 1;
      if (definitionId === 'cd1.tower.aa') this.state.counters.builtAA += 1;
    } else {
      const sourceAbility: AbilityId | undefined = definitionId === 'cd1.building.silo'
        ? 'missile'
        : definitionId === 'cd1.building.wall-factory'
          ? 'wall'
          : definitionId === 'cd1.building.laboratory'
            ? 'stop-time'
            : undefined;
      const firstSource = sourceAbility !== undefined && this.abilitySourceCount(sourceAbility) === 0;
      const building: BuildingState = { id: this.nextEntityId++, definitionId, cell, cost: definition.cost.value };
      this.state.buildings = [...this.state.buildings, building];
      if (firstSource) this.state.abilityCooldowns[sourceAbility] = 0;
    }
    this.state.counters.builtTotal += 1;
    this.refreshUnlocks();
    this.notice('build', `${structureLabel(definitionId)} deployed for $${definition.cost.value}.`);
    return true;
  }

  sellAt(x: number, y: number): boolean {
    if (this.state.paused) return false;
    const cell = this.toCell(x, y);
    const tower = this.state.towers.find((candidate) => this.occupiesCell(candidate, cell.x, cell.y));
    const building = this.state.buildings.find((candidate) => this.occupiesBuildingCell(candidate, cell.x, cell.y));
    const wall = this.state.walls.find((candidate) => candidate.cell.x === cell.x && candidate.cell.y === cell.y);
    if (!tower && !building && !wall) {
      this.notice('notice', 'Select a deployed defense node to sell.');
      return false;
    }
    const cost = tower?.cost ?? building?.cost ?? wall?.cost ?? 0;
    const refund = Math.round(cost * SELL_FRACTION.value);
    this.state.cash += refund;
    // A tower or 2x2 building can be supported by a wall. Sell that top structure first;
    // the next click exposes and sells the underlying wall.
    if (tower) this.state.towers = this.state.towers.filter((candidate) => candidate.id !== tower.id);
    else if (building) this.state.buildings = this.state.buildings.filter((candidate) => candidate.id !== building.id);
    else if (wall) this.state.walls = this.state.walls.filter((candidate) => candidate.id !== wall.id);
    this.refreshUnlocks();
    this.notice('sell', `Defense recycled for $${refund}.`);
    return true;
  }

  useAbility(ability: AbilityId, x: number, y: number): boolean {
    if (this.state.paused || !this.abilityReady(ability)) {
      this.notice('notice', 'Ability is unavailable or recharging.');
      return false;
    }
    if (ability === 'wall') return this.placeWall(x, y);
    if (ability === 'missile') {
      const targetPosition = { x: Math.max(0, Math.min(LOGICAL_BOARD_SIZE, x)), y: Math.max(0, Math.min(LOGICAL_BOARD_SIZE, y)) };
      this.state.projectiles = [...this.state.projectiles, {
        id: this.nextEntityId++, ownerId: 0, targetId: 0, targetPosition,
        position: { ...ABILITY_RULES.missile.launch }, speed: ABILITY_RULES.missile.speedPerTick, damage: ABILITY_RULES.missile.damage.value, color: 0xffe15b,
        angle: angleTo(ABILITY_RULES.missile.launch, targetPosition), turnRate: 0.1,
        piercesShield: true, kind: 'missile', splashRadius: ABILITY_RULES.missile.radius,
      }];
      this.state.missileActions += 1;
      this.visual('ability', ABILITY_RULES.missile.launch, {
        target: targetPosition, color: 0xffe15b, radius: ABILITY_RULES.missile.radius, ability: 'missile-cast', projectileKind: 'missile',
      });
    } else {
      const center = { x, y };
      this.state.enemies = this.state.enemies.map((enemy) => {
        if (distanceSquared(enemy.position, center) > ABILITY_RULES['stop-time'].radius ** 2) return enemy;
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'stopped', durationTicks: ABILITY_RULES['stop-time'].durationTicks, color: 0x77d8ff });
        return { ...enemy, stoppedTicks: Math.max(enemy.stoppedTicks, ABILITY_RULES['stop-time'].durationTicks) };
      });
      this.visual('ability', center, { color: 0x77d8ff, radius: ABILITY_RULES['stop-time'].radius, ability: 'stop-time' });
    }
    this.startAbilityCooldown(ability);
    this.refreshUnlocks();
    this.notice('notice', ability === 'missile' ? 'Silo missile launched.' : 'Stop Time field engaged for five seconds.');
    return true;
  }

  step(ticks = 1): void {
    for (let remaining = 0; remaining < ticks; remaining += 1) this.stepOnce();
  }

  private stepOnce(): void {
    if (this.state.paused || this.state.phase === 'victory' || this.state.phase === 'defeat') return;
    this.state.tick += 1;
    this.tickAbilityCooldowns();
    if (this.resolveWave(this.state.nextWaveIndex + 1) && this.state.nextWaveCountdownTicks > 0) this.state.nextWaveCountdownTicks -= 1;
    if (this.resolveWave(this.state.nextWaveIndex + 1) && this.state.nextWaveCountdownTicks <= 0) this.beginNextWave(false);
    if (this.state.phase === 'active') this.spawnDueEnemies();
    this.moveEnemies();
    this.updateTowers();
    this.updateProjectiles();
    this.removeDeadEnemies();
    this.finishWaveIfResolved();
  }

  private getScript(): readonly WaveDefinition[] { return WAVE_SCRIPTS[this.state.mapId][this.state.difficultyId]; }

  private resolveWave(absoluteWave: number) {
    return resolveWave(this.getScript(), this.state.difficultyId, this.state.runMode, absoluteWave);
  }

  private currentWave(): WaveDefinition | undefined {
    return this.state.activeWave ? this.resolveWave(this.state.activeWave.absoluteWave)?.definition : undefined;
  }

  private spawnDueEnemies(): void {
    const active = this.state.activeWave;
    const wave = this.currentWave();
    if (!active || !wave || active.spawned >= active.total || this.state.tick < active.nextSpawnTick) return;
    // The original program rows create their full unit group together. Retain a non-zero path for
    // any later authored encounter that explicitly requests staggered spawns.
    do {
      const group = wave.groups[active.groupIndex];
      this.spawnEnemy(group, active.groupSpawned);
      active.spawned += 1;
      active.groupSpawned += 1;
      active.nextSpawnTick = this.state.tick + wave.spawnEveryTicks.value;
      if (active.groupSpawned >= group.count) {
        active.groupIndex += 1;
        active.groupSpawned = 0;
      }
    } while (wave.spawnEveryTicks.value === 0 && active.spawned < active.total && active.groupIndex < wave.groups.length);
    if (active.groupIndex >= wave.groups.length) {
      active.groupIndex = wave.groups.length;
    }
  }

  private spawnEnemy(group: WaveGroup, waveIndex: number): void {
    const definition = enemyDefinition(group.domain, group.size, group.level);
    const map = MAPS[this.state.mapId];
    const path = group.domain === 'ground' ? this.createGroundPath(map, waveIndex) : this.createAirPath();
    const hpMultiplier = this.state.activeWave?.hpMultiplier ?? 1;
    const health = scaleEnemyHealth(definition.hp.value, hpMultiplier);
    const enemy: EnemyState = {
      id: this.nextEntityId++,
      definitionId: definition.id,
      domain: group.domain,
      size: group.size,
      level: group.level,
      hp: health,
      maxHp: health,
      reward: definition.reward.value,
      ...(this.state.runMode === 'infinite' ? { scoreMultiplier: hpMultiplier } : {}),
      speed: definition.speed.value * (group.effect === 'fast' ? 1.5 : 1),
      throttle: definition.throttlePerTick.value,
      turnRate: definition.turnPerTick.value,
      velocity: definition.speed.value * (group.effect === 'fast' ? 1.5 : 1),
      heading: angleTo(path[0], path[1] ?? path[0]),
      effect: group.effect,
      slowTicks: 0,
      confuseTicks: 0,
      stoppedTicks: 0,
      path,
      pathIndex: 1,
      position: cloneVec(path[0]),
      progress: 0,
    };
    this.state.enemies = [...this.state.enemies, enemy];
  }

  private moveEnemies(): void {
    const remaining: EnemyState[] = [];
    for (const enemy of this.state.enemies) {
      let current = enemy.effect === 'autorepair'
        ? { ...enemy, hp: Math.min(enemy.maxHp, enemy.hp + enemy.maxHp * 0.005) }
        : { ...enemy };
      const stopped = current.stoppedTicks > 0;
      const slowed = current.slowTicks > 0;
      const confused = current.confuseTicks > 0;
      let travelBudget = stopped ? 0 : current.speed * (slowed ? 0.75 : 1) * TICK_SECONDS;
      let hops = 0;
      while (current.pathIndex < current.path.length && hops < 32) {
        const target = current.path[current.pathIndex];
        const distance = Math.sqrt(distanceSquared(current.position, target));
        if (distance <= 0.0001) {
          current = { ...current, position: cloneVec(target), pathIndex: current.pathIndex + 1 };
          hops += 1;
          continue;
        }
        const desiredHeading = angleTo(current.position, target);
        const heading = approachAngle(current.heading, desiredHeading, current.turnRate * (confused ? CONFUSE_TURN_MULTIPLIER : 1));
        const alignment = confused ? Math.max(0, Math.cos(wrapAngle(desiredHeading - heading))) : 1;
        const baseVelocity = stopped ? 0 : current.speed * (slowed ? 0.75 : 1);
        const velocity = baseVelocity * alignment;
        if (travelBudget <= 0) {
          current = { ...current, heading, velocity };
          break;
        }
        // Position is constrained to the prevalidated ordered route segment. Heading still turns at its
        // recovered rate, but it cannot steer a ground unit diagonally through a canyon corner.
        const availableTravel = travelBudget * alignment;
        const travel = Math.min(availableTravel, distance);
        const position = moveTowards(current.position, target, travel);
        if (current.domain === 'ground' && !this.canGroundTraverse(current.position, position)) {
          // A wall can only race a stale segment in injected/replayed state: regenerate the exit-field route
          // immediately and keep this tick's travel budget. Normal movement never waits on a failed steer.
          const path = this.createGroundPath(MAPS[this.state.mapId], 0, current.position);
          if (path.length > 1) {
            current = { ...current, heading, velocity, path, pathIndex: 1 };
            continue;
          }
          // Preserve the position only if it is already isolated; this cannot arise from a legal wall placement.
          current = { ...current, heading, velocity: 0 };
          break;
        }
        current = { ...current, heading, velocity, position, progress: current.progress + travel };
        travelBudget = Math.max(0, availableTravel - travel);
        if (distance <= travel + 0.0001) {
          current = { ...current, position: cloneVec(target), pathIndex: current.pathIndex + 1 };
          hops += 1;
          continue;
        }
        break;
      }
      // Durations take effect for the full movement frame in which their value is positive, then tick down.
      current.slowTicks = Math.max(0, current.slowTicks - 1);
      current.confuseTicks = Math.max(0, current.confuseTicks - 1);
      current.stoppedTicks = Math.max(0, current.stoppedTicks - 1);
      if (current.pathIndex >= current.path.length) {
        this.state.lives -= LEAK_LIFE_DAMAGE.value;
        this.notice('leak', 'An enemy escaped the canyon. -1 life');
      } else {
        remaining.push(current);
      }
    }
    this.state.enemies = remaining;
    if (this.state.lives <= 0) {
      this.state.lives = 0;
      this.state.phase = 'defeat';
      this.state.activeWave = null;
      this.notice('result', 'Run lost. The canyon was overrun.');
    }
  }

  /** The path is allowed to run along a road edge, but never through rock or a blocking wall. */
  private canGroundTraverse(from: Vec2, to: Vec2): boolean {
    const steps = Math.max(1, Math.ceil(Math.hypot(to.x - from.x, to.y - from.y)));
    for (let step = 1; step <= steps; step += 1) {
      const fraction = step / steps;
      const x = from.x + (to.x - from.x) * fraction;
      const y = from.y + (to.y - from.y) * fraction;
      if (!this.isOpenGroundPoint(x, y)) return false;
    }
    return true;
  }

  private isOpenGroundPoint(x: number, y: number): boolean {
    if (x < 0 || x > LOGICAL_BOARD_SIZE || y < 0 || y > LOGICAL_BOARD_SIZE) return true;
    const axisCells = (coordinate: number): readonly number[] => {
      const lower = Math.floor(coordinate / CELL_SIZE);
      const onBoundary = Math.abs(coordinate - Math.round(coordinate / CELL_SIZE) * CELL_SIZE) <= 0.0001;
      return onBoundary ? [lower - 1, lower] : [lower];
    };
    return axisCells(x).some((cellX) => axisCells(y).some((cellY) =>
      cellX >= 0 && cellX < GRID_SIZE && cellY >= 0 && cellY < GRID_SIZE
      && MAPS[this.state.mapId].roadCells.includes(cellKey(cellX, cellY)) && !this.hasWall(cellX, cellY)));
  }

  private updateTowers(): void {
    const nextTowers: TowerState[] = [];
    for (const tower of this.state.towers) {
      const definition = TOWERS[tower.definitionId];
      const target = this.acquireTarget(tower);
      const next = { ...tower, cooldownTicks: Math.max(0, tower.cooldownTicks - 1), targetId: target?.id ?? null };
      if (target) {
        const targetAngle = angleTo(next.position, target.position);
        const alignedBeforeTurn = Math.abs(wrapAngle(targetAngle - next.angle)) < TOWER_FIRE_ALIGNMENT_RADIANS.value;
        next.angle = definition.turnRate.value === 0
          ? targetAngle
          : approachAngle(next.angle, targetAngle, definition.turnRate.value * TICK_SECONDS);
        const aligned = definition.turnRate.value === 0
          ? alignedBeforeTurn
          : Math.abs(wrapAngle(targetAngle - next.angle)) < TOWER_FIRE_ALIGNMENT_RADIANS.value;
        if (aligned && next.cooldownTicks === 0) {
          const color = this.projectileColor(definition.projectile);
          const isElectricPulse = definition.projectile === 'electricField';
          this.state.projectiles = [...this.state.projectiles, {
            id: this.nextEntityId++, ownerId: next.id, targetId: isElectricPulse ? 0 : target.id,
            position: cloneVec(next.position),
            speed: isElectricPulse ? 0 : definition.projectileSpeed.value || Math.sqrt(distanceSquared(next.position, target.position)) * FIXED_TICK_HZ,
            angle: angleTo(next.position, target.position),
            turnRate: definition.projectileTurnRate.value,
            damage: definition.damage.value,
            color,
            piercesShield: definition.piercesShield === true,
            kind: definition.projectile,
            splashRadius: isElectricPulse ? this.getEffectiveTowerRange(next.definitionId) : definition.splashRadius,
            ...(isElectricPulse ? { targetPosition: cloneVec(next.position) }
              : definition.projectile === 'ball' ? { targetPosition: cloneVec(target.position) } : {}),
            ...(definition.slow ? { slow: { percent: definition.slow.percent, ticks: definition.slow.ticks } } : {}),
            ...(definition.confuse ? { confuse: { percent: definition.confuse.percent, ticks: definition.confuse.ticks } } : {}),
            ...(definition.clearsEffects ? { clearsEffects: true } : {}),
            ...(definition.chainTargets ? { chainTargets: definition.chainTargets } : {}),
          }];
          this.visual('fire', next.position, {
            sourceId: next.id,
            target: target.position,
            projectileKind: definition.projectile,
            color,
          });
          next.cooldownTicks = definition.reloadTicks.value;
        }
      }
      nextTowers.push(next);
    }
    this.state.towers = nextTowers;
  }

  private acquireTarget(tower: TowerState): EnemyState | undefined {
    const definition = TOWERS[tower.definitionId];
    const domains = definition.domains;
    const range = this.getEffectiveTowerRange(tower.definitionId);
    const rangeSquared = range * range;
    const eligible = (enemy: EnemyState): boolean => enemy.hp > 0
      && domains.includes(enemy.domain)
      && distanceSquared(tower.position, enemy.position) <= rangeSquared;
    const locked = tower.targetId === null ? undefined : this.state.enemies.find((enemy) => enemy.id === tower.targetId);
    if (locked && eligible(locked)) return locked;
    return this.state.enemies
      .filter(eligible)
      .sort((a, b) => distanceSquared(a.position, tower.position) - distanceSquared(b.position, tower.position) || a.id - b.id)[0];
  }

  private updateProjectiles(): void {
    const nextProjectiles: ProjectileState[] = [];
    const enemyById = new Map(this.state.enemies.map((enemy) => [enemy.id, enemy]));
    for (const projectile of this.state.projectiles) {
      const target = enemyById.get(projectile.targetId);
      const targetPosition = projectile.kind === 'ball' ? projectile.targetPosition : target?.position ?? projectile.targetPosition;
      if (!targetPosition) continue;
      const distance = Math.sqrt(distanceSquared(projectile.position, targetPosition));
      const travel = projectile.speed * TICK_SECONDS;
      if (distance <= travel + PROJECTILE_HIT_RADIUS.value) {
        this.applyProjectileImpact(projectile, targetPosition, projectile.kind === 'ball' ? undefined : target);
      } else {
        const desiredAngle = angleTo(projectile.position, targetPosition);
        const angle = approachAngle(projectile.angle, desiredAngle, projectile.turnRate);
        nextProjectiles.push({ ...projectile, angle, position: { x: projectile.position.x + Math.cos(angle) * travel, y: projectile.position.y + Math.sin(angle) * travel } });
      }
    }
    this.state.projectiles = nextProjectiles;
  }

  private applyProjectileImpact(projectile: ProjectileState, position: Vec2, directTarget?: EnemyState): void {
    this.visual('impact', position, {
      projectileKind: projectile.kind,
      color: projectile.color,
      radius: projectile.splashRadius,
      ...(projectile.kind === 'missile' ? { ability: 'missile-impact' as const } : {}),
    });
    const candidates = projectile.splashRadius > 0
      ? this.state.enemies.filter((enemy) =>
        (projectile.kind !== 'electricField' || enemy.domain === 'ground')
        && distanceSquared(enemy.position, position) <= projectile.splashRadius * projectile.splashRadius)
      : directTarget ? [directTarget] : [];
    const chain = projectile.chainTargets && directTarget
      ? this.state.enemies.filter((enemy) => enemy.domain === directTarget.domain && enemy.id !== directTarget.id)
        .sort((a, b) => distanceSquared(a.position, directTarget.position) - distanceSquared(b.position, directTarget.position))
        .slice(0, projectile.chainTargets - 1)
      : [];
    const affected = [...new Map([...candidates, ...chain].map((enemy) => [enemy.id, enemy])).values()];
    affected.forEach((enemy) => {
      const damage = enemy.effect === 'shield' && !projectile.piercesShield ? projectile.damage * 0.1 : projectile.damage;
      enemy.hp -= damage;
      if (projectile.slow) {
        enemy.slowTicks = Math.max(enemy.slowTicks, projectile.slow.ticks);
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'slow', durationTicks: projectile.slow.ticks, color: 0x68dc76, projectileKind: projectile.kind });
      }
      if (projectile.confuse) {
        enemy.confuseTicks = Math.max(enemy.confuseTicks, projectile.confuse.ticks);
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'confuse', durationTicks: projectile.confuse.ticks, color: 0xe7a14b, projectileKind: projectile.kind });
      }
      if (projectile.clearsEffects) {
        enemy.effect = undefined;
        enemy.slowTicks = 0;
        enemy.confuseTicks = 0;
        this.visual('status', enemy.position, { sourceId: enemy.id, status: 'clear', color: 0xe3a4ff, projectileKind: projectile.kind });
      }
    });
  }

  private removeDeadEnemies(): void {
    const remaining: EnemyState[] = [];
    for (const enemy of this.state.enemies) {
      if (enemy.hp > 0) {
        remaining.push(enemy);
        continue;
      }
      const reward = enemy.reward * (1 + this.countBuildings('cd1.building.recycler') * 0.5);
      this.state.cash += reward;
      this.state.score += Math.round(reward * SCORE_PER_REWARD.value * (enemy.scoreMultiplier ?? 1));
      this.state.counters.kills += 1;
      this.state.counters.earned += reward;
      if (enemy.domain === 'air') this.state.counters.destroyedAir += 1;
      this.refreshUnlocks();
      this.notice('kill', `Hostile neutralized. +$${reward}`);
    }
    this.state.enemies = remaining;
  }

  private finishWaveIfResolved(): void {
    const active = this.state.activeWave;
    if (!active || active.spawned < active.total || this.state.enemies.length > 0 || this.state.projectiles.length > 0) return;
    const finished = this.resolveWave(active.absoluteWave);
    if (!finished) return;
    this.state.activeWave = null;
    this.state.completedWaves = Math.max(this.state.completedWaves, active.absoluteWave);
    if (this.state.runMode === 'fixed' && !this.resolveWave(this.state.nextWaveIndex + 1)) {
      this.state.phase = 'victory';
      this.state.score = Math.round((this.state.counters.earned * SCORE_PER_REWARD.value + this.state.lives * 10_000) * DIFFICULTIES[this.state.difficultyId].scoreMultiplier.value);
      this.notice('result', `Canyon secured. ${finished.absoluteWave} attacks survived.`);
    } else {
      this.state.phase = 'prewave';
      const next = this.resolveWave(this.state.nextWaveIndex + 1);
      const transition = next && next.baseWave === 1 && next.completedCycles > 0
        ? ` Cycle ${next.cycle} ready: enemy HP ${next.hpPercent}%.`
        : ' Ready for the next attack.';
      this.notice('wave', `Attack ${finished.absoluteWave} resolved.${transition}`);
    }
  }

  /** A top structure may be placed on canyon rock, an existing wall, or a mixed 2x2 support footprint. */
  canPlaceStructure(definitionId: CatalogueId, x: number, y: number): boolean {
    const anchor = this.toCell(x, y);
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[definitionId];
    for (let offsetY = 0; offsetY < height; offsetY += 1) {
      for (let offsetX = 0; offsetX < width; offsetX += 1) {
        const cellX = anchor.x + offsetX;
        const cellY = anchor.y + offsetY;
        if ((!this.isBuildableCell(cellX, cellY) && !this.hasWall(cellX, cellY)) || this.isStructureOccupied(cellX, cellY)) return false;
      }
    }
    return true;
  }

  private occupiesCell(tower: TowerState, x: number, y: number): boolean {
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[tower.definitionId];
    return x >= tower.cell.x && x < tower.cell.x + width && y >= tower.cell.y && y < tower.cell.y + height;
  }

  private occupiesBuildingCell(building: BuildingState, x: number, y: number): boolean {
    const [width, height] = STRUCTURE_FOOTPRINT_CELLS[building.definitionId];
    return x >= building.cell.x && x < building.cell.x + width && y >= building.cell.y && y < building.cell.y + height;
  }

  private isStructureOccupied(x: number, y: number): boolean {
    return this.state.towers.some((tower) => this.occupiesCell(tower, x, y))
      || this.state.buildings.some((building) => this.occupiesBuildingCell(building, x, y));
  }

  private hasWall(x: number, y: number): boolean { return this.state.walls.some((wall) => wall.cell.x === x && wall.cell.y === y); }

  private countTowers(definitionId?: TowerId): number {
    return definitionId ? this.state.towers.filter((tower) => tower.definitionId === definitionId).length : this.state.towers.length;
  }

  private countBuildings(definitionId?: BuildingId): number {
    return definitionId ? this.state.buildings.filter((building) => building.definitionId === definitionId).length : this.state.buildings.length;
  }

  private refreshUnlocks(): void {
    const unlocks = this.state.unlocked;
    // Unlocking a predecessor can make an already-satisfied successor active in the same deterministic refresh.
    for (let pass = 0; pass < CATALOGUE.length; pass += 1) {
      let changed = false;
      this.getUnlockProgress().forEach((progress) => {
        if (progress.active && progress.current >= progress.target && !unlocks[progress.id]) {
          unlocks[progress.id] = true;
          changed = true;
        }
      });
      if (!changed) return;
    }
  }

  private abilitySourceCount(ability: AbilityId): number {
    if (ability === 'missile') return this.countBuildings('cd1.building.silo');
    if (ability === 'wall') return this.countBuildings('cd1.building.wall-factory');
    return this.countBuildings('cd1.building.laboratory');
  }

  private startAbilityCooldown(ability: AbilityId): void {
    this.state.abilityCooldowns[ability] = ACTIVE_COOLDOWN_TICKS;
  }

  private tickAbilityCooldowns(): void {
    // Original CAbility.tick: each matching source contributes one timer tick.
    abilityIds.forEach((ability) => {
      this.state.abilityCooldowns[ability] = Math.max(0, this.state.abilityCooldowns[ability] - this.abilitySourceCount(ability));
    });
  }

  private creditSkippedCooldowns(skippedTicks: number): void {
    if (skippedTicks <= 0) return;
    // Early release must grant the same recharge that waiting through these ticks would.
    abilityIds.forEach((ability) => {
      this.state.abilityCooldowns[ability] = Math.max(
        0,
        this.state.abilityCooldowns[ability] - skippedTicks * this.abilitySourceCount(ability),
      );
    });
  }

  private placeWall(x: number, y: number): boolean {
    const cell = this.toCell(x, y);
    const map = MAPS[this.state.mapId];
    if (!this.canPlaceWall(x, y)) {
      const isRoute = cell.x > 0 && cell.x < GRID_SIZE - 1 && cell.y > 0 && cell.y < GRID_SIZE - 1 && map.roadCells.includes(cellKey(cell.x, cell.y));
      this.notice('notice', isRoute ? 'Wall placement is blocked by an occupant or would seal the canyon.' : 'Wall placement needs an open interior route cell.');
      return false;
    }
    const candidate: WallState = { id: this.nextEntityId++, cell, cost: 0 };
    this.state.walls = [...this.state.walls, candidate];
    this.rerouteGroundEnemies();
    this.startAbilityCooldown('wall');
    this.refreshUnlocks();
    this.notice('build', 'Wall deployed; route remains open.');
    return true;
  }

  /** Shared wall action hit-test for both the simulation and its ability preview. */
  canPlaceWall(x: number, y: number): boolean {
    const cell = this.toCell(x, y);
    const map = MAPS[this.state.mapId];
    if (cell.x <= 0 || cell.x >= GRID_SIZE - 1 || cell.y <= 0 || cell.y >= GRID_SIZE - 1) return false;
    if (!map.roadCells.includes(cellKey(cell.x, cell.y)) || this.hasWall(cell.x, cell.y) || this.isStructureOccupied(cell.x, cell.y)) return false;
    if (this.state.enemies.some((enemy) => Math.floor(enemy.position.x / CELL_SIZE) === cell.x && Math.floor(enemy.position.y / CELL_SIZE) === cell.y)) return false;
    return this.hasReachableGroundRoute([...this.state.walls, { id: -1, cell, cost: 0 }]);
  }

  private hasReachableGroundRoute(walls: readonly WallState[]): boolean {
    const map = MAPS[this.state.mapId];
    const wallCells = new Set(walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const distance = this.groundExitDistance(map, walls);
    for (let y = 0; y < GRID_SIZE; y += 1) {
      for (let x = 0; x < GRID_SIZE; x += 1) {
        if (map.roadCells.includes(cellKey(x, y)) && !wallCells.has(cellKey(x, y)) && distance[y][x] < 0) return false;
      }
    }
    return true;
  }

  private projectileColor(kind: string): number {
    return ({ rocket: 0xffb15a, ball: 0xe9ce72, electricField: 0x75d7ff, laserBeam: 0xf2505a, nulifyBall: 0xe3a4ff, aaShell: 0x9ee9ff, slowShot: 0x75c886, chainBlast: 0xf8ec78, gaussRail: 0x83e5ff, confuseBall: 0xd98948 } as Record<string, number>)[kind] ?? 0xffe15b;
  }

  private isBuildableCell(x: number, y: number): boolean {
    if (x < 0 || x >= GRID_SIZE || y < 0 || y >= GRID_SIZE) return false;
    return MAPS[this.state.mapId].surfaceRows[y]?.[x] === 'T';
  }

  private toCell(x: number, y: number): { x: number; y: number } {
    return { x: Math.floor(x / CELL_SIZE), y: Math.floor(y / CELL_SIZE) };
  }

  /** Original CMap route model: edge candidates, exit-seeded flood field, then ordered orthogonal descent. */
  private createGroundPath(map: MapDefinition, waveIndex: number, fromPosition?: Vec2, pendingEntry?: Vec2): readonly Vec2[] {
    const blocked = new Set(this.state.walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const road = (x: number, y: number): boolean => x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE && map.roadCells.includes(cellKey(x, y)) && !blocked.has(cellKey(x, y));
    const entries: { x: number; y: number }[] = [];
    for (let index = 0; index < GRID_SIZE; index += 1) {
      if (road(index, 0)) entries.push({ x: index, y: 0 });
      if (road(0, index)) entries.push({ x: 0, y: index });
    }
    const distance = this.groundExitDistance(map, this.state.walls);
    const reachableEntries = entries.filter((cell) => distance[cell.y][cell.x] >= 0);
    const currentCell = fromPosition ? this.toCell(fromPosition.x, fromPosition.y) : undefined;
    const beforeEntry = Boolean(fromPosition && (fromPosition.x < 0 || fromPosition.y < 0));
    const retainedEntry = beforeEntry && pendingEntry
      ? reachableEntries.find((cell) => {
        const boundary = this.entryBoundary(cell);
        return boundary.x === pendingEntry.x && boundary.y === pendingEntry.y;
      })
      : undefined;
    const start = fromPosition && !beforeEntry
      ? currentCell && road(currentCell.x, currentCell.y) && distance[currentCell.y][currentCell.x] >= 0 ? currentCell : undefined
      : retainedEntry ?? reachableEntries[this.randomInt(reachableEntries.length)];
    if (!start) return fromPosition ? [cloneVec(fromPosition)] : [{ x: -CELL_SIZE / 2, y: -CELL_SIZE / 2 }];
    const path: Vec2[] = fromPosition
      ? [cloneVec(fromPosition)]
      : [start.y === 0
        ? { x: this.cellCenter(start.x, start.y).x + this.randomInt(16) - 8, y: -CELL_SIZE / 2 - 35 * waveIndex }
        : { x: -CELL_SIZE / 2 - 35 * waveIndex, y: this.cellCenter(start.x, start.y).y + this.randomInt(16) - 8 }];
    if (!fromPosition || beforeEntry) path.push(this.entryBoundary(start));
    const routeCells = [start];
    let cursor = start;
    while (distance[cursor.y][cursor.x] > 0) {
      const nextCells = this.orthogonalNeighbors(cursor.x, cursor.y).filter((candidate) => distance[candidate.y]?.[candidate.x] === distance[cursor.y][cursor.x] - 1);
      const next = nextCells[0];
      if (!next) break;
      routeCells.push(next);
      cursor = next;
    }
    for (let index = 0; index < routeCells.length; index += 1) {
      const cell = routeCells[index];
      const next = routeCells[index + 1];
      const outgoing = next ? this.sharedBoundary(cell, next) : this.exitBoundary(cell);
      const incomingDirection = index > 0
        ? { x: cell.x - routeCells[index - 1].x, y: cell.y - routeCells[index - 1].y }
        : (!fromPosition || beforeEntry) ? (cell.y === 0 ? { x: 0, y: 1 } : { x: 1, y: 0 }) : undefined;
      const outgoingDirection = next
        ? { x: next.x - cell.x, y: next.y - cell.y }
        : cell.y === GRID_SIZE - 1 ? { x: 0, y: 1 } : { x: 1, y: 0 };
      if (incomingDirection && incomingDirection.x * outgoingDirection.x + incomingDirection.y * outgoingDirection.y === 0) {
        this.appendTurnCurve(path, outgoing, this.cellCenter(cell.x, cell.y));
      } else {
        path.push(outgoing);
      }
    }
    path.push(cursor.y === GRID_SIZE - 1
      ? { x: this.cellCenter(cursor.x, cursor.y).x, y: LOGICAL_BOARD_SIZE + CELL_SIZE / 2 }
      : { x: LOGICAL_BOARD_SIZE + CELL_SIZE / 2, y: this.cellCenter(cursor.x, cursor.y).y });
    return path;
  }

  /** Replace a sharp boundary-to-boundary corner with a short in-cell quadratic turn. */
  private appendTurnCurve(path: Vec2[], outgoing: Vec2, control: Vec2): void {
    const incoming = path[path.length - 1];
    for (let sample = 1; sample <= TURN_CURVE_SAMPLES; sample += 1) {
      const t = sample / TURN_CURVE_SAMPLES;
      const inverse = 1 - t;
      path.push({
        x: inverse * inverse * incoming.x + 2 * inverse * t * control.x + t * t * outgoing.x,
        y: inverse * inverse * incoming.y + 2 * inverse * t * control.y + t * t * outgoing.y,
      });
    }
  }

  private groundExitDistance(map: MapDefinition, walls: readonly WallState[]): number[][] {
    const wallCells = new Set(walls.map((wall) => cellKey(wall.cell.x, wall.cell.y)));
    const road = (x: number, y: number): boolean => x >= 0 && x < GRID_SIZE && y >= 0 && y < GRID_SIZE && map.roadCells.includes(cellKey(x, y)) && !wallCells.has(cellKey(x, y));
    const distance = Array.from({ length: GRID_SIZE }, () => Array<number>(GRID_SIZE).fill(-1));
    const queue: { x: number; y: number }[] = [];
    for (let index = 0; index < GRID_SIZE; index += 1) {
      if (road(index, GRID_SIZE - 1)) queue.push({ x: index, y: GRID_SIZE - 1 });
      if (road(GRID_SIZE - 1, index)) queue.push({ x: GRID_SIZE - 1, y: index });
    }
    queue.forEach((cell) => { distance[cell.y][cell.x] = 0; });
    for (let head = 0; head < queue.length; head += 1) {
      const current = queue[head];
      for (const neighbor of this.orthogonalNeighbors(current.x, current.y)) {
        if (!road(neighbor.x, neighbor.y) || distance[neighbor.y][neighbor.x] !== -1) continue;
        distance[neighbor.y][neighbor.x] = distance[current.y][current.x] + 1;
        queue.push(neighbor);
      }
    }
    return distance;
  }

  private entryBoundary(cell: { x: number; y: number }): Vec2 {
    return cell.y === 0 ? { x: this.cellCenter(cell.x, cell.y).x, y: 0 } : { x: 0, y: this.cellCenter(cell.x, cell.y).y };
  }

  private exitBoundary(cell: { x: number; y: number }): Vec2 {
    return cell.y === GRID_SIZE - 1 ? { x: this.cellCenter(cell.x, cell.y).x, y: LOGICAL_BOARD_SIZE } : { x: LOGICAL_BOARD_SIZE, y: this.cellCenter(cell.x, cell.y).y };
  }

  private sharedBoundary(a: { x: number; y: number }, b: { x: number; y: number }): Vec2 {
    return { x: (a.x + b.x + 1) * CELL_SIZE / 2, y: (a.y + b.y + 1) * CELL_SIZE / 2 };
  }

  private rerouteGroundEnemies(): void {
    const map = MAPS[this.state.mapId];
    this.state.enemies = this.state.enemies.map((enemy) => {
      if (enemy.domain !== 'ground' || enemy.pathIndex >= enemy.path.length) return enemy;
      if (enemy.position.x > LOGICAL_BOARD_SIZE || enemy.position.y > LOGICAL_BOARD_SIZE) return enemy;
      const path = this.createGroundPath(map, 0, enemy.position, enemy.path[enemy.pathIndex]);
      return { ...enemy, path, pathIndex: Math.min(1, path.length) };
    });
  }

  private createAirPath(): readonly Vec2[] {
    const column = 4 + this.randomInt(6);
    const x = column * CELL_SIZE + CELL_SIZE / 2;
    return [{ x, y: -CELL_SIZE / 2 }, { x, y: LOGICAL_BOARD_SIZE + CELL_SIZE / 2 }];
  }

  private orthogonalNeighbors(x: number, y: number): readonly { x: number; y: number }[] {
    return [{ x, y: y - 1 }, { x: x - 1, y }, { x: x + 1, y }, { x, y: y + 1 }]
      .filter((cell) => cell.x >= 0 && cell.x < GRID_SIZE && cell.y >= 0 && cell.y < GRID_SIZE);
  }

  private cellCenter(x: number, y: number): Vec2 { return { x: x * CELL_SIZE + CELL_SIZE / 2, y: y * CELL_SIZE + CELL_SIZE / 2 }; }

  private randomInt(maximumExclusive: number): number {
    if (maximumExclusive <= 1) return 0;
    this.randomState = (Math.imul(this.randomState, 1664525) + 1013904223) >>> 0;
    return this.randomState % maximumExclusive;
  }

  private visual(kind: VisualEvent['kind'], position: Vec2, details: Omit<VisualEvent, 'id' | 'tick' | 'kind' | 'position'> = {}): void {
    const event: VisualEvent = {
      id: this.nextVisualEventId++,
      tick: this.state.tick,
      kind,
      position: cloneVec(position),
      ...details,
      ...(details.target ? { target: cloneVec(details.target) } : {}),
    };
    this.state.visualEvents = [...this.state.visualEvents.slice(-(MAX_VISUAL_EVENTS - 1)), event];
  }

  private notice(type: SimEvent['type'], message: string): void {
    const event: SimEvent = { tick: this.state.tick, type, message };
    this.state.events = [...this.state.events.slice(-(MAX_EVENTS - 1)), event];
    this.state.lastNotice = message;
  }
}
